import AbstractFilterView, { type AbstractFilterViewArgs } from '@UI/Helpers/Filters/AbstractFilterView';

/** Screen-level status; composed into `isActive`/`pendingInvitation` server params. */
export type UserStatusFilter = 'active' | 'invited' | 'deactivated';

/** The only two columns the server accepts a sort direction for (`orderByLastName`/`orderByEmail`). */
export type UserSortField = 'lastName' | 'email';

export type UserFilterViewArgs = AbstractFilterViewArgs & {
	readonly search?: string;
	readonly organizationId?: number | null;
	readonly roleId?: number | null;
	readonly facilityId?: number | null;
	readonly status?: UserStatusFilter | null;
	readonly sortField?: UserSortField | null;
	readonly sortOrder?: 1 | -1 | null;
};

/**
 * `status` is the one field of this screen that does not pass through to the query string as-is:
 * it is derived from two independent server params (`isActive`, `pendingInvitation`), so it
 * is composed here rather than left to the caller.
 */
class UserFilterView extends AbstractFilterView<UserFilterViewArgs> {
	readonly search: string;
	readonly organizationId: number | null;
	readonly roleId: number | null;
	readonly facilityId: number | null;
	readonly status: UserStatusFilter | null;
	readonly sortField: UserSortField | null;
	readonly sortOrder: 1 | -1 | null;

	constructor(args: UserFilterViewArgs) {
		super(args);

		this.search = args.search ?? '';
		this.organizationId = args.organizationId ?? null;
		this.roleId = args.roleId ?? null;
		this.facilityId = args.facilityId ?? null;
		this.status = args.status ?? null;
		this.sortField = args.sortField ?? null;
		this.sortOrder = args.sortOrder ?? null;

		Object.freeze(this);
	}

	static createDefault(): UserFilterView {
		return new UserFilterView({});
	}

	override get queryParams(): URLSearchParams {
		const params = super.queryParams;

		if (this.search.length > 0) {
			params.set('search', this.search);
		}

		if (this.organizationId !== null) {
			params.set('organizationId', String(this.organizationId));
		}

		if (this.roleId !== null) {
			params.set('roleId', String(this.roleId));
		}

		if (this.facilityId !== null) {
			params.set('facilityId', String(this.facilityId));
		}

		if (this.status === 'active') {
			params.set('isActive', 'true');
			params.set('pendingInvitation', 'false');
		} else if (this.status === 'invited') {
			params.set('isActive', 'true');
			params.set('pendingInvitation', 'true');
		} else if (this.status === 'deactivated') {
			params.set('isActive', 'false');
		}

		if (this.sortField !== null && this.sortOrder !== null) {
			const direction = this.sortOrder === 1 ? 'ASC' : 'DESC';
			const orderParam = this.sortField === 'lastName' ? 'orderByLastName' : 'orderByEmail';
			params.set(orderParam, direction);
		}

		return params;
	}

	/** Number of criteria that narrow the results; sorting never counts. */
	get activeFilterCount(): number {
		return [
			this.search.length > 0,
			this.organizationId !== null,
			this.roleId !== null,
			this.facilityId !== null,
			this.status !== null,
		].filter(Boolean).length;
	}

	setSearch(search: string): this {
		return this.cloneWith({ search, page: 0 });
	}

	/** Resets `facilityId`: the facility dropdown's options follow the selected organization, so a
	 * facility already picked from a different organization would no longer make sense. */
	setOrganizationId(organizationId: number | null): this {
		return this.cloneWith({ organizationId, facilityId: null, page: 0 });
	}

	setRoleId(roleId: number | null): this {
		return this.cloneWith({ roleId, page: 0 });
	}

	setFacilityId(facilityId: number | null): this {
		return this.cloneWith({ facilityId, page: 0 });
	}

	setStatus(status: UserStatusFilter | null): this {
		return this.cloneWith({ status, page: 0 });
	}

	setSort(sortField: UserSortField | null, sortOrder: 1 | -1 | null): this {
		return this.cloneWith({ sortField, sortOrder, page: 0 });
	}

	hasActiveFilters(): boolean {
		return this.activeFilterCount > 0;
	}
}

export default UserFilterView;
